import { inject, Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";

import { forkJoin, map, Observable, take } from "rxjs";

import { HttpBaseService } from "../http/http-base.service";
import {
  CreateSchoolPayload,
  SchoolData,
  SchoolDetail,
  SchoolInfoData,
  UpdateSchoolPayload
} from "../../models/core/school.interface";
import { ManagerListFilters, ManagerListItem } from "../../models/core/manager.interface";
import { StagesWithGradeResponse } from "../../models/core/network.interface";
import { CustomSelectData } from "../../models/core/select.interface";
import { PaginatedResponse } from "../../models/paginated-response.interface";
import { DEFAULT_PAGINATION, Pagination } from "../../models/pagination.interface";
import { buildManagerListParams } from "../../utils/manager-list-params.util";
import { sharedEnvironment } from "../../../environments/shared-environment";

import { GradeService } from "./grade.service";

/**
 * Paginação de COMBO: uma página só, grande o bastante para a lista inteira de uma rede caber. Vem
 * do mesmo precedente do `FILTER_PAGINATION` de `network.service.ts` — 100 é o teto prático usado
 * pelos outros seletores do admin. Rede com mais escolas que isso continua sem busca server-side.
 */
const SELECT_PAGINATION: Pagination = { pageNumber: 1, pageSize: 100 };

@Injectable({
  providedIn: "root"
})
export class SchoolService extends HttpBaseService {
  private readonly gradeService = inject(GradeService);

  protected override getApiEndpoint(): string[] {
    return [sharedEnvironment.serviceUrl, "api", "core/school"];
  }

  public getSchoolList(
    txName = "",
    inStatus: boolean | null = true,
    idNetwork: string[] = [],
    idSubNetwork: string[] = [],
    sorts: string[] = [],
    pagination: Pagination = DEFAULT_PAGINATION
  ): Observable<PaginatedResponse<SchoolData>> {
    let params = new HttpParams()
      .set("txName", txName)
      .set("Sorts", sorts.join(","))
      .set("pageNumber", pagination.pageNumber.toString())
      .set("pageSize", pagination.pageSize.toString());

    if (inStatus !== null) {
      params = params.set("inStatus", inStatus.toString());
    }

    if (idNetwork && idNetwork.length > 0) {
      idNetwork.forEach((id) => (params = params.append("idNetwork", id)));
    }

    if (idSubNetwork && idSubNetwork.length > 0) {
      idSubNetwork.forEach((id) => (params = params.append("idSubNetwork", id)));
    }

    return this.http.get<PaginatedResponse<SchoolData>>(this.buildPath(), { params }).pipe(take(1));
  }

  public getSchoolEssentials(idSchool: string): Observable<SchoolDetail> {
    return this.getSchoolDetail(idSchool);
  }

  public getSchoolDetail(idSchool: string): Observable<SchoolDetail> {
    return this.http.get<SchoolDetail>(this.buildPath(["detail", idSchool])).pipe(take(1));
  }

  /**
   * Gestores de uma ESCOLA, paginados (DEV-916, CA 08). Único dos três níveis em que a coluna de
   * Ano Escolar tem dado: o vínculo gestor↔ano vive em `UserConfigGrade`, cuja chave carrega a
   * escola.
   *
   * Substitui a leitura de `getSchoolInfoData().managers`, que trazia a lista inteira sem
   * paginação, sem CPF e sem filtro.
   */
  public getSchoolManagers(
    idSchool: string,
    pagination: Pagination = DEFAULT_PAGINATION,
    filters: ManagerListFilters = {}
  ): Observable<PaginatedResponse<ManagerListItem>> {
    return this.http
      .get<PaginatedResponse<ManagerListItem>>(this.buildPath([idSchool, "manager"]), {
        params: buildManagerListParams(pagination, filters)
      })
      .pipe(take(1));
  }

  public getSchoolDetailWithFilters(
    idSchool: string,
    idNetwork: string | null = null,
    inStatus = true,
    txName: string | null = null,
    pagination: Pagination = DEFAULT_PAGINATION
  ): Observable<SchoolDetail> {
    const params: Record<string, string> = {
      inStatus: inStatus.toString(),
      pageNumber: pagination.pageNumber.toString(),
      pageSize: pagination.pageSize.toString()
    };

    if (idNetwork !== null && idNetwork.trim() !== "") {
      params["idNetwork"] = idNetwork;
    }

    if (txName !== null && txName.trim() !== "") {
      params["txName"] = txName;
    }

    return this.http
      .get<SchoolDetail>(this.buildPath(["detail", idSchool]), { params })
      .pipe(take(1));
  }

  public updateSchool(payload: UpdateSchoolPayload): Observable<SchoolDetail> {
    return this.http.put<SchoolDetail>(this.buildPath(), payload).pipe(take(1));
  }

  /**
   * Escolas para montar COMBO — lista inteira de uma vez, não uma página.
   *
   * `SELECT_PAGINATION` existe porque o default do `getSchoolList` é `pageSize: 10`: paginação de
   * grade, não de combo. Com ele, uma rede de centenas de escolas oferecia dez, sem aviso e sem
   * critério visível (a ordem é a do banco), e quem procurava uma escola específica simplesmente não
   * a encontrava. Mesmo desenho do `FILTER_PAGINATION` de Rede/Regional (`network.service.ts:20`).
   *
   * `inStatus` default `true` preserva os consumidores que só querem escola ativa. Quem lista
   * histórico — a Integração — passa `null` para incluir a inativa, que continua tendo envios.
   */
  public getSchoolSelectData(
    idNetwork: string[] = [],
    idSubNetwork: string[] = [],
    inStatus: boolean | null = true
  ): Observable<{
    schools: SchoolData[];
    selectData: CustomSelectData[];
  }> {
    return this.getSchoolList("", inStatus, idNetwork, idSubNetwork, [], SELECT_PAGINATION).pipe(
      map((response) => {
        const schools = response.content ?? [];
        const selectData = schools.map((item) => ({
          value: String(item.idSchool),
          label: item.txName ?? ""
        }));
        return { schools, selectData };
      })
    );
  }

  public getSchoolInfoData(idSchool: string): Observable<SchoolInfoData> {
    return forkJoin({
      schoolDetail: this.getSchoolDetail(idSchool),
      allGrades: this.gradeService.getGrades()
    }).pipe(
      map(({ schoolDetail, allGrades }) => {
        const schoolGradeIds = new Set(
          (schoolDetail.schoolGrade ?? schoolDetail.grades ?? []).map((g) => g.idGrade)
        );

        const stageMap = new Map<string, StagesWithGradeResponse>();

        for (const grade of allGrades) {
          if (!schoolGradeIds.has(grade.idGrade)) continue;
          const stageId = grade.idStage ?? "unknown";
          const existing = stageMap.get(stageId);

          if (existing) {
            existing.grade.push({ idGrade: grade.idGrade, txGrade: grade.txGrade });
          } else {
            stageMap.set(stageId, {
              idStage: stageId,
              txStageName: grade.txStage ?? "Outros",
              inStatus: true,
              grade: [{ idGrade: grade.idGrade, txGrade: grade.txGrade }]
            });
          }
        }

        return {
          stages: Array.from(stageMap.values()),
          managers: schoolDetail.schoolManagers ?? []
        };
      })
    );
  }

  public createSchool(payload: CreateSchoolPayload): Observable<void> {
    return this.http.post<void>(this.buildPath(), payload).pipe(take(1));
  }
}
